/*
  Predictive search for Luxe & Gloss.

  The input keeps DOM focus while aria-activedescendant identifies the active
  suggestion. Requests are encoded, cancellable, and guarded so an older
  response can never replace a newer query.
*/

(() => {
  const SEARCH_DELAY = 250
  const ALLOWED_PREDICTIVE_RESOURCE_TYPES = new Set(['query', 'product', 'collection', 'page'])

  function sanitizePredictiveResourceTypes (resourceTypes) {
    const sanitizedTypes = String(resourceTypes || '')
      .split(',')
      .map(resourceType => resourceType.trim())
      .filter(resourceType => ALLOWED_PREDICTIVE_RESOURCE_TYPES.has(resourceType))

    return [...new Set(sanitizedTypes)].join(',') || 'query,product'
  }

  function buildPredictiveSearchUrl (baseUrl, searchTerm, resourceTypes, origin = window.location.origin) {
    const url = new URL(baseUrl, origin)
    url.searchParams.set('q', searchTerm)
    url.searchParams.set('resources[type]', sanitizePredictiveResourceTypes(resourceTypes))
    url.searchParams.set('resources[limit]', '10')
    url.searchParams.set('section_id', 'predictive-search')

    return url.origin === origin
      ? `${url.pathname}${url.search}`
      : url.toString()
  }

  class PredictiveSearch extends HTMLElement {
    constructor () {
      super()

      this.handleInput = this.handleInput.bind(this)
      this.handleKeydown = this.handleKeydown.bind(this)
      this.handlePointerMove = this.handlePointerMove.bind(this)
      this.handleRetry = this.handleRetry.bind(this)
      this.handleSubmit = this.handleSubmit.bind(this)
      this.handleDrawerShown = this.handleDrawerShown.bind(this)
      this.handleDrawerHidden = this.handleDrawerHidden.bind(this)
    }

    connectedCallback () {
      if (this.initialized) return

      this.form = this.querySelector('form.search-form')
      this.input = this.form?.querySelector('input[type="search"]')
      this.results = this.form?.querySelector('[data-lg-search-results]')
      this.status = this.form?.querySelector('[data-lg-search-status]')
      this.emptyContainer = this.querySelector('[data-lg-search-empty]')
      this.loadingContainer = this.querySelector('[data-lg-search-loading]')
      this.errorContainer = this.querySelector('[data-lg-search-error]')
      this.retryButton = this.querySelector('[data-lg-search-retry]')
      this.drawer = this.closest('#offcanvas-search')

      if (!this.form || !this.input || !this.results || !this.status) return

      this.initialized = true
      this.activeIndex = -1
      this.requestSequence = 0
      this.options = []

      this.input.addEventListener('input', this.handleInput)
      this.input.addEventListener('keydown', this.handleKeydown)
      this.results.addEventListener('pointermove', this.handlePointerMove)
      this.form.addEventListener('submit', this.handleSubmit)
      this.retryButton?.addEventListener('click', this.handleRetry)
      this.drawer?.addEventListener('shown.bs.offcanvas', this.handleDrawerShown)
      this.drawer?.addEventListener('hidden.bs.offcanvas', this.handleDrawerHidden)

      this.showEmptyState()
    }

    disconnectedCallback () {
      if (!this.initialized) return

      this.cancelPendingRequest()
      this.input.removeEventListener('input', this.handleInput)
      this.input.removeEventListener('keydown', this.handleKeydown)
      this.results.removeEventListener('pointermove', this.handlePointerMove)
      this.form.removeEventListener('submit', this.handleSubmit)
      this.retryButton?.removeEventListener('click', this.handleRetry)
      this.drawer?.removeEventListener('shown.bs.offcanvas', this.handleDrawerShown)
      this.drawer?.removeEventListener('hidden.bs.offcanvas', this.handleDrawerHidden)
      this.initialized = false
    }

    handleDrawerShown () {
      this.input.focus()

      if (this.input.value.trim()) {
        this.scheduleSearch()
      }
    }

    handleDrawerHidden () {
      this.cancelPendingRequest()
      this.collapseResults()
    }

    handleInput (event) {
      if (event.isComposing) return
      this.scheduleSearch()
    }

    scheduleSearch () {
      const searchTerm = this.input.value.trim()

      this.cancelPendingRequest()

      if (!searchTerm) {
        this.showEmptyState()
        return
      }

      this.showLoadingState()
      this.searchTimer = window.setTimeout(() => {
        this.getSearchResults(searchTerm)
      }, SEARCH_DELAY)
    }

    async getSearchResults (searchTerm = this.input.value.trim()) {
      if (!searchTerm) {
        this.showEmptyState()
        return
      }

      const requestId = ++this.requestSequence
      const controller = new AbortController()
      this.abortController = controller
      const url = buildPredictiveSearchUrl(
        window.Shopify.routes.predictive_search_url,
        searchTerm,
        this.dataset.resourcesType
      )

      try {
        const response = await fetch(url, {
          headers: { Accept: 'text/html' },
          signal: controller.signal
        })

        if (!response.ok) {
          throw new Error(`Predictive search returned ${response.status}`)
        }

        const responseText = await response.text()
        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html')
        const responseSection = responseDocument.querySelector('#shopify-section-predictive-search')

        if (!responseSection) {
          throw new Error('Predictive search response was incomplete')
        }

        if (this.isStaleRequest(requestId, searchTerm)) return

        this.results.innerHTML = responseSection.innerHTML
        this.showResults()
      } catch (error) {
        if (error.name === 'AbortError' || this.isStaleRequest(requestId, searchTerm)) return
        this.showErrorState()
      } finally {
        if (this.abortController === controller) {
          this.abortController = undefined
        }
      }
    }

    isStaleRequest (requestId, searchTerm) {
      return requestId !== this.requestSequence || searchTerm !== this.input.value.trim()
    }

    cancelPendingRequest () {
      window.clearTimeout(this.searchTimer)
      this.searchTimer = undefined
      this.requestSequence++
      this.abortController?.abort()
      this.abortController = undefined
    }

    showResults () {
      const resultsRoot = this.results.querySelector('#predictive-search-results')
      const listbox = this.results.querySelector('#predictive-search-options')

      if (!resultsRoot || !listbox) {
        this.showErrorState()
        return
      }

      this.options = Array.from(listbox.querySelectorAll('[role="option"]'))
      this.options.forEach(option => {
        option.setAttribute('aria-selected', 'false')
        option.setAttribute('tabindex', '-1')
      })
      this.activeIndex = -1

      this.emptyContainer?.setAttribute('hidden', '')
      this.loadingContainer?.setAttribute('hidden', '')
      this.errorContainer?.setAttribute('hidden', '')
      this.results.removeAttribute('hidden')
      this.form.setAttribute('aria-busy', 'false')
      this.input.setAttribute('aria-expanded', 'true')
      this.input.removeAttribute('aria-activedescendant')
      this.status.textContent = resultsRoot.dataset.resultsText || 'Search suggestions are available.'
    }

    showEmptyState () {
      this.resetResultsMarkup()
      this.results.setAttribute('hidden', '')
      this.loadingContainer?.setAttribute('hidden', '')
      this.errorContainer?.setAttribute('hidden', '')
      this.emptyContainer?.removeAttribute('hidden')
      this.form?.setAttribute('aria-busy', 'false')
      this.options = []
      this.activeIndex = -1
      this.input?.setAttribute('aria-expanded', 'false')
      this.input?.removeAttribute('aria-activedescendant')
      if (this.status) this.status.textContent = ''
    }

    showLoadingState () {
      this.results.setAttribute('hidden', '')
      this.emptyContainer?.setAttribute('hidden', '')
      this.errorContainer?.setAttribute('hidden', '')
      this.loadingContainer?.removeAttribute('hidden')
      this.form.setAttribute('aria-busy', 'true')
      this.options = []
      this.activeIndex = -1
      this.input.setAttribute('aria-expanded', 'false')
      this.input.removeAttribute('aria-activedescendant')
      this.status.textContent = 'Searching…'
    }

    showErrorState () {
      this.resetResultsMarkup()
      this.results.setAttribute('hidden', '')
      this.emptyContainer?.setAttribute('hidden', '')
      this.loadingContainer?.setAttribute('hidden', '')
      this.errorContainer?.removeAttribute('hidden')
      this.form.setAttribute('aria-busy', 'false')
      this.options = []
      this.activeIndex = -1
      this.input.setAttribute('aria-expanded', 'false')
      this.input.removeAttribute('aria-activedescendant')
      this.status.textContent = 'Search suggestions are unavailable. Press Enter to search the full catalog.'
    }

    resetResultsMarkup () {
      this.results.innerHTML = '<div id="predictive-search-options" role="listbox" aria-label="Search suggestions"></div>'
    }

    collapseResults () {
      this.results.setAttribute('hidden', '')
      this.input.setAttribute('aria-expanded', 'false')
      this.clearActiveOption()
    }

    clearActiveOption () {
      this.options.forEach(option => option.setAttribute('aria-selected', 'false'))
      this.activeIndex = -1
      this.input.removeAttribute('aria-activedescendant')
    }

    setActiveOption (nextIndex) {
      if (!this.options.length) return

      const normalizedIndex = (nextIndex + this.options.length) % this.options.length
      this.options.forEach((option, index) => {
        option.setAttribute('aria-selected', index === normalizedIndex ? 'true' : 'false')
      })

      const option = this.options[normalizedIndex]
      this.activeIndex = normalizedIndex
      this.input.setAttribute('aria-activedescendant', option.id)
      option.scrollIntoView?.({ block: 'nearest' })
    }

    handleKeydown (event) {
      if (event.isComposing) return

      if (event.key === 'ArrowDown') {
        if (this.input.getAttribute('aria-expanded') !== 'true') return
        event.preventDefault()
        this.setActiveOption(this.activeIndex + 1)
      } else if (event.key === 'ArrowUp') {
        if (this.input.getAttribute('aria-expanded') !== 'true') return
        event.preventDefault()
        this.setActiveOption(this.activeIndex < 0 ? this.options.length - 1 : this.activeIndex - 1)
      } else if (event.key === 'Home' && this.activeIndex >= 0) {
        event.preventDefault()
        this.setActiveOption(0)
      } else if (event.key === 'End' && this.activeIndex >= 0) {
        event.preventDefault()
        this.setActiveOption(this.options.length - 1)
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault()
        this.options[this.activeIndex].click()
      } else if (event.key === 'Escape') {
        this.cancelPendingRequest()
        this.collapseResults()
      }
    }

    handlePointerMove (event) {
      const option = event.target.closest?.('[role="option"]')
      const index = this.options.indexOf(option)

      if (index >= 0 && index !== this.activeIndex) {
        this.setActiveOption(index)
      }
    }

    handleRetry () {
      const searchTerm = this.input.value.trim()
      if (!searchTerm) {
        this.input.focus()
        return
      }

      this.showLoadingState()
      this.getSearchResults(searchTerm)
    }

    handleSubmit (event) {
      if (this.input.value.trim()) return

      event.preventDefault()
      this.status.textContent = 'Enter a search term.'
      this.input.focus()
    }
  }

  window.LGSearch = Object.assign(window.LGSearch || {}, { buildPredictiveSearchUrl })

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch)
  }
})()
